/*
 * 戒导航 · 主题偏好持久化（jiedh/theme-persist）前台初始化
 * =============================================================================
 * 职责：让用户选择的主题（浅色 / 深色 / 高对比 / 跟随系统）在「退出登录后」依然保留。
 *
 * 背景（核心源码事实，见 vendor/flarum/core/js/src/common/Application.tsx）：
 *
 *   private initColorScheme(forumDefault: string | null = null): void {
 *     forumDefault ??= app.forum.attribute('colorScheme') ?? 'auto';
 *     this.allowUserColorScheme = forumDefault === 'auto';
 *     const userConfiguredPreference = this.session.user?.preferences()?.colorScheme;
 *     let scheme;
 *     if (this.allowUserColorScheme) {
 *       scheme = userConfiguredPreference ?? (this.session.user ? undefined : sessionStorage.getItem('colorScheme') ?? undefined);
 *     }
 *     scheme ||= forumDefault;              // forumDefault 默认 'auto'
 *     this.setColorScheme(scheme);
 *     ...
 *   }
 *
 *   · 登录态：scheme = 账号偏好 preferences.colorScheme（本站 admin 为 'light'）→ 浅色 ✓
 *   · 游客态：账号偏好不存在 → 只能读 sessionStorage；而「登录后切换主题」这条路径
 *             （ThemeSwitcher.select / SettingsPage.colorSchemeItems）只调用了
 *             savePreferences()，从不写 sessionStorage，因此退出登录时 sessionStorage 为空
 *             → 回落到 'auto' → getSystemColorSchemePreference() 读到系统深色 → 变深色 ✗
 *
 * 修复要点：把「用户的选择」写进 localStorage（真正的持久化存储），并在游客启动时补种给核心。
 *
 * 为什么不去改 vendor/：
 *   项目硬约束 —— vendor/ 为 composer 管理的核心代码，升级时必须保持原样。
 *   因此这里用「运行时包装（monkey patch）实例方法」的方式实现，不改任何核心源码；
 *   一旦核心升级改了实现，本扩展最多退化成「无效果」，不会报错、不会破坏站点。
 *
 * 覆盖的入口（两处都会调用 app.setColorScheme，故一处包装即可全覆盖）：
 *   1) 顶栏主题切换器 vendor/flarum/core/js/src/forum/components/ThemeSwitcher.tsx 的 select()
 *   2) 个人设置页 vendor/flarum/core/js/src/forum/components/SettingsPage.tsx 的 colorSchemeItems()
 *   另有启动时的 this.initColorScheme() → this.setColorScheme(...) 也会经过包装（这是关键：
 *   即便用户本次没有手动切换，登录启动时也会把账号偏好镜像到 localStorage）。
 *
 * 存储键名沿用核心既有的 'colorScheme'，与核心 sessionStorage 的键保持一致，
 * 便于核心未来若改为读取 localStorage 时本扩展可无痛退役。
 */
import app from 'flarum/forum/app';

/** 与 Flarum 核心保持一致的存储键名（核心游客态用的是 sessionStorage.getItem('colorScheme')）。 */
const STORAGE_KEY = 'colorScheme';

/**
 * 读取浏览器持久化的主题偏好。
 *
 * 用 try/catch 包裹的原因：部分浏览器在「无痕模式 / 站点数据被禁用 / 磁盘配额已满」时，
 * 访问 localStorage 会直接抛异常。此处必须静默降级，绝不能因为存储不可用而中断整个论坛的启动。
 */
function readPersisted() {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch (e) {
    return null;
  }
}

/** 写入浏览器持久化的主题偏好；同样静默降级，存储不可用时仅失去持久化能力，不影响其它功能。 */
function persist(scheme) {
  try {
    localStorage.setItem(STORAGE_KEY, scheme);
  } catch (e) {
    /* 忽略：存储不可用（无痕模式等）时属于可接受的降级 */
  }
}

/**
 * 安装主题持久化的两个包装点。
 */
export default function persistColorScheme() {
  /*
   * ---------------------------------------------------------------------------
   * 包装点 A：镜像写入 —— app.setColorScheme()
   * ---------------------------------------------------------------------------
   * 注意写入的是「逻辑值」而不是「解析值」：
   *   · 用户选「跟随系统」时 scheme === 'auto'，我们存 'auto'（而不是此刻解析出的 'light'/'dark'），
   *     这样退出登录后仍然是「跟随系统」语义，不会把系统偏好误固化成某个具体主题。
   * 顺序说明：先持久化，再交给核心的方法去设置 this.colorScheme 与 data-theme 属性。
   */
  const originalSetColorScheme = app.setColorScheme.bind(app);
  app.setColorScheme = function (scheme) {
    persist(scheme);
    originalSetColorScheme(scheme);
  };

  /*
   * ---------------------------------------------------------------------------
   * 包装点 B：游客兜底 —— app.initColorScheme()
   * ---------------------------------------------------------------------------
   * 核心在游客态只会读取 sessionStorage，而 sessionStorage 是「会话级」的，
   * 无法承载「用户偏好」这种长期语义。这里在核心真正读取之前，
   * 把 localStorage 中的持久偏好补种进 sessionStorage，
   * 从而在不改动核心任何一行代码的前提下，让游客态复用用户上次的选择。
   *
   * 为什么必须在 initColorScheme 里补种（而不能在初始化器里提前补种）：
   *   Application.boot() 的执行顺序是 —— 先 initialize()（跑所有初始化器），
   *   之后才创建 this.session（见核心源码 boot() 中 session 赋值在 initialize() 之后）。
   *   因此初始化器执行时还拿不到 app.session，无法判断当前是登录还是游客；
   *   而 initColorScheme() 恰好发生在 mount() 阶段，此时 session 已经就绪，
   *   并且这是核心读取 sessionStorage 之前的最后一个时机，时机最准确。
   *
   * 兼容性：当论坛后台把默认主题固定为具体主题（allowUserColorScheme === false）时，
   *   核心本就会忽略 sessionStorage 直接使用论坛默认值，因此本补种不会产生任何干扰。
   */
  const originalInitColorScheme = app.initColorScheme.bind(app);
  app.initColorScheme = function (forumDefault) {
    // app.session 理论上此时已就绪；用可选链兜底，避免极端时序下抛错。
    if (!app.session?.user) {
      const saved = readPersisted();
      if (saved) {
        try {
          // 仅在 sessionStorage 尚无值时补种，避免覆盖用户在当前会话里的最新选择。
          if (!sessionStorage.getItem(STORAGE_KEY)) {
            sessionStorage.setItem(STORAGE_KEY, saved);
          }
        } catch (e) {
          /* 忽略：存储不可用时静默降级 */
        }
      }
    }
    originalInitColorScheme(forumDefault);
  };
}

// 初始化器 name 必须与扩展 ID 对应（本包 ID 为 jiedh/theme-persist → CLI 形式 jiedh-theme-persist），
// 它同时用于 Flarum 的错误归因（app.currentInitializerExtension）。
// 这里不设置优先级：本扩展与其它扩展互不影响，使用默认顺序即可。
app.initializers.add('jiedh-theme-persist', () => {
  persistColorScheme();
});